import { ref, watchEffect } from 'vue';

// Theme preference: follow the OS by default, or an explicit light/dark the
// user picked from the account menu. index.html applies the stored value
// before first paint; this module keeps it in sync afterwards.

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'net3-theme';

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export const themePreference = ref<ThemePreference>(readStored());

watchEffect(() => {
  const root = document.documentElement;
  const v = themePreference.value;
  if (v === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', v);
  try {
    if (v === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // Private mode etc. — the attribute still applies for this page load.
  }
});

/** The theme actually in effect right now (resolving "system"). */
export function resolvedTheme(): 'light' | 'dark' {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Calls `fn` whenever the effective theme may have changed. Returns a disposer. */
export function onThemeChange(fn: () => void): () => void {
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener('change', fn);
  const observer = new MutationObserver(fn);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => {
    mq?.removeEventListener('change', fn);
    observer.disconnect();
  };
}
